/**
 * Token Management Service for Cognito Authentication.
 *
 * Manages ID, Access, and Refresh tokens:
 * - ID and Access tokens are stored in memory only (Zustand store state)
 * - Refresh token stored in localStorage for web (OS keychain on desktop via IPC later)
 * - Auto-refreshes ID token 5 minutes before expiry
 * - Handles 30-day refresh token expiry (redirect to login)
 *
 * @see Requirements 7.5, 10.7
 */

import { Amplify } from 'aws-amplify';
import {
  signIn,
  signUp,
  signOut,
  confirmSignUp,
  resendSignUpCode,
  fetchAuthSession,
  type SignInOutput,
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { getAmplifyConfig } from '@/config/amplify';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface AuthTokens {
  idToken: string;
  accessToken: string;
  expiresAt: number; // Unix timestamp (ms) when ID token expires
}

export interface TokenManagerState {
  tokens: AuthTokens | null;
  isAuthenticated: boolean;
  isRefreshing: boolean;
  error: string | null;
}

export type AuthChangeListener = (state: TokenManagerState) => void;

// ─── Constants ───────────────────────────────────────────────────────────────

/** Refresh token 5 minutes before expiry. */
const REFRESH_BUFFER_MS = 5 * 60 * 1000;

/** Refresh token storage key for web (localStorage). */
const REFRESH_TOKEN_KEY = 'journal_app_refresh_token';

/** Refresh token timestamp key to track 30-day expiry. */
const REFRESH_TOKEN_ISSUED_KEY = 'journal_app_refresh_token_issued';

/** Refresh token max lifetime: 30 days in ms. */
const REFRESH_TOKEN_MAX_LIFETIME_MS = 30 * 24 * 60 * 60 * 1000;

// ─── Token Manager ───────────────────────────────────────────────────────────

/**
 * TokenManager handles all token lifecycle management.
 * It keeps ID/Access tokens in memory and persists only the refresh token.
 */
class TokenManagerImpl {
  private state: TokenManagerState = {
    tokens: null,
    isAuthenticated: false,
    isRefreshing: false,
    error: null,
  };

  private refreshTimerId: ReturnType<typeof setTimeout> | null = null;
  private listeners: Set<AuthChangeListener> = new Set();
  private initialized = false;

  /**
   * Initialize Amplify and attempt to restore session from stored refresh token.
   * Must be called once at app startup.
   */
  async configureAuth(): Promise<void> {
    console.log('[AUTH] configureAuth() called, initialized:', this.initialized);
    if (this.initialized) {
      console.log('[AUTH] configureAuth() already initialized, returning early');
      return;
    }

    // Amplify.configure() may have already been called in main.tsx,
    // but calling it again with the same config is safe (idempotent).
    const config = getAmplifyConfig();
    console.log('[AUTH] Amplify.configure() with config:', JSON.stringify(config, null, 2));
    Amplify.configure(config);
    this.initialized = true;

    // Try to restore session — handles both stored refresh tokens and OAuth redirects
    console.log('[AUTH] configureAuth() calling tryRestoreSession()...');
    await this.tryRestoreSession();
    console.log('[AUTH] configureAuth() complete. Final state:', JSON.stringify(this.state));
  }

  /**
   * Attempt to restore the session using a stored refresh token or OAuth redirect.
   * If the refresh token has expired (>30 days), clear it and require re-login.
   * Also handles OAuth redirect callbacks where the auth code is in the URL.
   */
  private async tryRestoreSession(): Promise<void> {
    console.log('[AUTH] tryRestoreSession() called');
    console.log('[AUTH] Current URL:', window.location.href);

    // Check if the stored refresh token has exceeded its 30-day lifetime
    const refreshToken = this.getStoredRefreshToken();
    console.log('[AUTH] Stored refresh token:', refreshToken);
    console.log('[AUTH] Refresh token expired?', this.isRefreshTokenExpired());

    if (refreshToken && this.isRefreshTokenExpired()) {
      console.log('[AUTH] Refresh token expired (>30 days), clearing');
      this.clearStoredRefreshToken();
      this.updateState({ tokens: null, isAuthenticated: false, error: null });
      return;
    }

    // Detect OAuth redirect: if the URL contains ?code= and state= params,
    // Amplify is processing the authorization code exchange asynchronously.
    // We must wait for that to complete before calling fetchAuthSession().
    const isOAuth = this.isOAuthRedirect();
    console.log('[AUTH] Is OAuth redirect?', isOAuth);

    if (isOAuth) {
      try {
        console.log('[AUTH] Waiting for OAuth completion...');
        await this.waitForOAuthCompletion();
        console.log('[AUTH] OAuth completion succeeded');
        return;
      } catch (err) {
        console.error('[AUTH] OAuth completion failed:', err);
        // OAuth flow failed — clean up URL params and fall through to normal session check
        this.cleanOAuthParams();
      }
    }

    // Normal session restoration: fetch tokens from Amplify's internal storage.
    // This handles restoring sessions from cookies/localStorage.
    console.log('[AUTH] Attempting normal session restoration via fetchAndStoreTokens()...');
    try {
      await this.fetchAndStoreTokens();
      console.log('[AUTH] fetchAndStoreTokens() succeeded. State:', JSON.stringify(this.state));
    } catch (err) {
      console.log('[AUTH] fetchAndStoreTokens() failed:', err);
      // If we had a stored refresh token, try force-refreshing
      if (refreshToken) {
        console.log('[AUTH] Have refresh token, trying performTokenRefresh()...');
        try {
          await this.performTokenRefresh();
          console.log('[AUTH] performTokenRefresh() succeeded');
        } catch (refreshErr) {
          console.error('[AUTH] performTokenRefresh() also failed:', refreshErr);
          this.clearStoredRefreshToken();
          this.updateState({ tokens: null, isAuthenticated: false, error: null });
        }
      } else {
        console.log('[AUTH] No refresh token available. User needs to login.');
        // No session available — user needs to login
        this.updateState({ tokens: null, isAuthenticated: false, error: null });
      }
    }
  }

  /**
   * Check if the current URL contains OAuth redirect parameters (code and state).
   * This indicates Cognito has redirected back after authentication.
   */
  private isOAuthRedirect(): boolean {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.has('code') && params.has('state');
    } catch {
      return false;
    }
  }

  /**
   * Wait for Amplify to complete the OAuth authorization code exchange.
   * Uses Hub to listen for the signedIn or signInWithRedirect_failure events.
   * Times out after 10 seconds to prevent indefinite loading.
   */
  private async waitForOAuthCompletion(): Promise<void> {
    console.log('[AUTH] waitForOAuthCompletion() called');
    return new Promise<void>((resolve, reject) => {
      const timeout = setTimeout(() => {
        console.log('[AUTH] waitForOAuthCompletion() TIMED OUT after 10s');
        unsubscribe();
        reject(new Error('OAuth completion timed out'));
      }, 10_000);

      const unsubscribe = Hub.listen('auth', async (data) => {
        const { event } = data.payload;
        console.log('[AUTH] Hub auth event received:', event);

        if (event === 'signedIn' || event === 'signInWithRedirect') {
          clearTimeout(timeout);
          unsubscribe();
          console.log('[AUTH] OAuth sign-in event detected, fetching tokens...');
          // OAuth exchange completed — now fetch and store tokens
          try {
            await this.fetchAndStoreTokens();
            this.cleanOAuthParams();
            resolve();
          } catch (err) {
            console.error('[AUTH] fetchAndStoreTokens after OAuth failed:', err);
            reject(err);
          }
        } else if (
          event === 'signInWithRedirect_failure' ||
          event === 'tokenRefresh_failure'
        ) {
          clearTimeout(timeout);
          unsubscribe();
          console.error('[AUTH] OAuth failure event:', event);
          this.cleanOAuthParams();
          reject(new Error('OAuth sign-in failed'));
        }
      });

      // Also try fetching the session directly in case Amplify already
      // completed the exchange before our listener was set up (race condition).
      // Use a small delay to give Amplify a moment to process.
      setTimeout(async () => {
        try {
          console.log('[AUTH] Checking session directly (500ms fallback)...');
          const session = await fetchAuthSession({ forceRefresh: false });
          console.log('[AUTH] Fallback session check - has idToken?', !!session.tokens?.idToken);
          if (session.tokens?.idToken) {
            clearTimeout(timeout);
            unsubscribe();
            await this.fetchAndStoreTokens();
            this.cleanOAuthParams();
            resolve();
          }
        } catch (err) {
          console.log('[AUTH] Fallback session check failed:', err);
          // Still waiting for Hub event — do nothing
        }
      }, 500);
    });
  }

  /**
   * Remove OAuth query parameters from the URL without triggering a page reload.
   * This cleans up the ?code=&state= params after processing the redirect.
   */
  private cleanOAuthParams(): void {
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('code');
      url.searchParams.delete('state');
      const cleanUrl = url.pathname + (url.search || '');
      window.history.replaceState({}, '', cleanUrl);
    } catch {
      // Best effort — don't break the app if URL manipulation fails
    }
  }

  /**
   * Login with email and password via Cognito.
   */
  async login(email: string, password: string): Promise<void> {
    console.log('[AUTH] login() called with email:', email);
    try {
      this.updateState({ error: null });

      // Amplify v6 throws UserAlreadyAuthenticatedException if a (possibly
      // stale) session is still present — e.g. after an interrupted logout or
      // an expired-but-not-cleared session. Defensively clear any existing
      // session first so a fresh sign-in always succeeds.
      try {
        await signOut();
      } catch {
        // No existing session (or sign-out failed) — safe to continue.
      }

      const result: SignInOutput = await signIn({
        username: email,
        password,
      });
      console.log('[AUTH] signIn result:', JSON.stringify({ isSignedIn: result.isSignedIn, nextStep: result.nextStep }));

      if (result.isSignedIn) {
        console.log('[AUTH] login() - signIn succeeded, fetching tokens...');
        await this.fetchAndStoreTokens();
        console.log('[AUTH] login() complete. isAuthenticated:', this.state.isAuthenticated);
      } else if (result.nextStep?.signInStep === 'CONFIRM_SIGN_UP') {
        console.log('[AUTH] login() - email not verified');
        throw new Error('EMAIL_NOT_VERIFIED');
      } else {
        console.log('[AUTH] login() - login incomplete, nextStep:', result.nextStep);
        throw new Error('LOGIN_INCOMPLETE');
      }
    } catch (error) {
      console.error('[AUTH] login() error:', error);
      const message = error instanceof Error ? error.message : 'Invalid credentials';
      this.updateState({ error: message, isAuthenticated: false, tokens: null });
      throw error;
    }
  }

  /**
   * Register a new user with email and password.
   */
  async register(email: string, password: string): Promise<void> {
    try {
      this.updateState({ error: null });

      await signUp({
        username: email,
        password,
        options: {
          userAttributes: { email },
        },
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Registration failed';
      this.updateState({ error: message });
      throw error;
    }
  }

  /**
   * Confirm email verification with the code sent to the user.
   */
  async confirmEmail(email: string, code: string): Promise<void> {
    try {
      this.updateState({ error: null });

      await confirmSignUp({
        username: email,
        confirmationCode: code,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Verification failed';
      this.updateState({ error: message });
      throw error;
    }
  }

  /**
   * Resend the verification email.
   */
  async resendVerification(email: string): Promise<void> {
    try {
      this.updateState({ error: null });

      await resendSignUpCode({ username: email });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to resend verification';
      this.updateState({ error: message });
      throw error;
    }
  }

  /**
   * Initiate social login (Google or Apple).
   * On web, this triggers an OAuth redirect flow managed by Amplify.
   */
  async socialLogin(_provider: 'google' | 'apple'): Promise<void> {
    // Social login via Amplify's hosted UI will redirect the browser.
    // After redirect back, tokens are available via fetchAuthSession.
    // This is handled by calling fetchAndStoreTokens after redirect.
    try {
      this.updateState({ error: null });
      // The actual redirect is handled by Amplify's signInWithRedirect.
      // After redirect back, call configureAuth() which calls tryRestoreSession().
      const { signInWithRedirect } = await import('aws-amplify/auth');
      await signInWithRedirect({
        provider: _provider === 'google' ? 'Google' : 'Apple',
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Social login failed';
      this.updateState({ error: message });
      throw error;
    }
  }

  /**
   * Refresh the ID token using the stored refresh token.
   * Called automatically before token expiry and can be called manually.
   */
  async refreshToken(): Promise<void> {
    if (this.state.isRefreshing) return;

    // Check 30-day refresh token expiry
    if (this.isRefreshTokenExpired()) {
      this.handleRefreshTokenExpired();
      return;
    }

    try {
      this.updateState({ isRefreshing: true });
      await this.performTokenRefresh();
    } catch {
      this.handleRefreshTokenExpired();
    } finally {
      this.updateState({ isRefreshing: false });
    }
  }

  /**
   * Logout: clear all tokens and invalidate the Cognito session.
   */
  async logout(): Promise<void> {
    this.stopAutoRefresh();

    try {
      await signOut();
    } catch {
      // Best-effort signout on server; always clear local state
    }

    this.clearStoredRefreshToken();
    this.updateState({
      tokens: null,
      isAuthenticated: false,
      error: null,
    });
  }

  /**
   * Get the current ID token for API authorization headers.
   * Returns null if not authenticated.
   * Triggers a refresh if the token is about to expire.
   */
  async getIdToken(): Promise<string | null> {
    if (!this.state.tokens) return null;

    // If token is expired or about to expire, refresh first
    const now = Date.now();
    if (now >= this.state.tokens.expiresAt - REFRESH_BUFFER_MS) {
      try {
        await this.refreshToken();
      } catch {
        return null;
      }
    }

    return this.state.tokens?.idToken ?? null;
  }

  /**
   * Check if the user is currently authenticated with valid tokens.
   */
  isAuthenticated(): boolean {
    if (!this.state.tokens) return false;

    // Check if refresh token has expired (30-day session)
    if (this.isRefreshTokenExpired()) {
      return false;
    }

    return this.state.isAuthenticated;
  }

  /**
   * Get the current state (for Zustand store integration).
   */
  getState(): TokenManagerState {
    return { ...this.state };
  }

  /**
   * Subscribe to auth state changes.
   * Returns an unsubscribe function.
   */
  subscribe(listener: AuthChangeListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Clear the session (used by AuthGuard on inactivity timeout).
   */
  clearSession(): void {
    this.stopAutoRefresh();
    this.clearStoredRefreshToken();
    this.updateState({
      tokens: null,
      isAuthenticated: false,
      error: null,
    });
  }

  // ─── Private Methods ─────────────────────────────────────────────────────

  /**
   * Fetch tokens from Amplify's auth session and store them in memory.
   */
  private async fetchAndStoreTokens(): Promise<void> {
    console.log('[AUTH] fetchAndStoreTokens() called');
    const session = await fetchAuthSession({ forceRefresh: false });
    console.log('[AUTH] fetchAuthSession result - has tokens?', !!session.tokens);
    console.log('[AUTH] fetchAuthSession result - has idToken?', !!session.tokens?.idToken);
    console.log('[AUTH] fetchAuthSession result - has accessToken?', !!session.tokens?.accessToken);

    const idToken = session.tokens?.idToken?.toString();
    const accessToken = session.tokens?.accessToken?.toString();

    if (!idToken || !accessToken) {
      console.log('[AUTH] fetchAndStoreTokens() - No tokens returned, throwing');
      throw new Error('No tokens returned from session');
    }

    // Parse token expiry from JWT payload
    const expiresAt = this.getTokenExpiry(idToken);
    console.log('[AUTH] Token expires at:', new Date(expiresAt).toISOString());

    const tokens: AuthTokens = {
      idToken,
      accessToken,
      expiresAt,
    };

    // Store refresh token in persistent storage
    // Amplify manages the refresh token internally, but we track the issued time
    // for our 30-day expiry check
    this.storeRefreshTokenTimestamp();

    this.updateState({
      tokens,
      isAuthenticated: true,
      error: null,
    });
    console.log('[AUTH] fetchAndStoreTokens() complete - isAuthenticated: true');

    this.scheduleAutoRefresh(expiresAt);
  }

  /**
   * Force-refresh tokens from Cognito using the refresh token.
   */
  private async performTokenRefresh(): Promise<void> {
    const session = await fetchAuthSession({ forceRefresh: true });

    const idToken = session.tokens?.idToken?.toString();
    const accessToken = session.tokens?.accessToken?.toString();

    if (!idToken || !accessToken) {
      throw new Error('Token refresh failed - no tokens returned');
    }

    const expiresAt = this.getTokenExpiry(idToken);

    const tokens: AuthTokens = {
      idToken,
      accessToken,
      expiresAt,
    };

    this.updateState({
      tokens,
      isAuthenticated: true,
      error: null,
    });

    this.scheduleAutoRefresh(expiresAt);
  }

  /**
   * Schedule automatic token refresh 5 minutes before the ID token expires.
   */
  private scheduleAutoRefresh(expiresAt: number): void {
    this.stopAutoRefresh();

    const now = Date.now();
    const refreshAt = expiresAt - REFRESH_BUFFER_MS;
    const delay = Math.max(refreshAt - now, 0);

    this.refreshTimerId = setTimeout(async () => {
      try {
        await this.refreshToken();
      } catch {
        // Token refresh failed; user will need to re-authenticate
        this.handleRefreshTokenExpired();
      }
    }, delay);
  }

  /**
   * Stop the auto-refresh timer.
   */
  private stopAutoRefresh(): void {
    if (this.refreshTimerId !== null) {
      clearTimeout(this.refreshTimerId);
      this.refreshTimerId = null;
    }
  }

  /**
   * Handle when the refresh token has expired (30-day limit).
   * Clears all tokens and signals the need for re-authentication.
   */
  private handleRefreshTokenExpired(): void {
    this.stopAutoRefresh();
    this.clearStoredRefreshToken();
    this.updateState({
      tokens: null,
      isAuthenticated: false,
      isRefreshing: false,
      error: 'SESSION_EXPIRED',
    });
  }

  /**
   * Extract the expiry timestamp from a JWT token.
   */
  private getTokenExpiry(jwtToken: string): number {
    try {
      const payload = jwtToken.split('.')[1];
      if (!payload) return Date.now() + 3600 * 1000; // Default 1 hour

      const decoded = JSON.parse(atob(payload)) as { exp?: number };
      if (decoded.exp) {
        return decoded.exp * 1000; // Convert seconds to ms
      }
    } catch {
      // Fall through to default
    }
    // Default: assume 1 hour from now
    return Date.now() + 3600 * 1000;
  }

  /**
   * Store the timestamp when the refresh token was issued/last used.
   * Used to track the 30-day lifetime.
   */
  private storeRefreshTokenTimestamp(): void {
    try {
      const existing = localStorage.getItem(REFRESH_TOKEN_ISSUED_KEY);
      if (!existing) {
        // Only set on first issue, not on refresh
        localStorage.setItem(REFRESH_TOKEN_ISSUED_KEY, Date.now().toString());
      }
      // Mark that we have a valid refresh token
      localStorage.setItem(REFRESH_TOKEN_KEY, 'active');
    } catch {
      // localStorage may not be available; tokens still work in memory
    }
  }

  /**
   * Get the stored refresh token indicator.
   * Returns a truthy string if a refresh token was stored.
   */
  private getStoredRefreshToken(): string | null {
    try {
      return localStorage.getItem(REFRESH_TOKEN_KEY);
    } catch {
      return null;
    }
  }

  /**
   * Check if the refresh token has exceeded its 30-day lifetime.
   */
  private isRefreshTokenExpired(): boolean {
    try {
      const issuedStr = localStorage.getItem(REFRESH_TOKEN_ISSUED_KEY);
      if (!issuedStr) return false; // No timestamp means we can't determine expiry

      const issuedAt = parseInt(issuedStr, 10);
      if (isNaN(issuedAt)) return true;

      return Date.now() - issuedAt > REFRESH_TOKEN_MAX_LIFETIME_MS;
    } catch {
      return false;
    }
  }

  /**
   * Clear stored refresh token data from localStorage.
   */
  private clearStoredRefreshToken(): void {
    try {
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_ISSUED_KEY);
    } catch {
      // Best effort
    }
  }

  /**
   * Update internal state and notify all listeners.
   */
  private updateState(partial: Partial<TokenManagerState>): void {
    const prevAuth = this.state.isAuthenticated;
    this.state = { ...this.state, ...partial };
    if (this.state.isAuthenticated !== prevAuth) {
      console.log('[AUTH] State transition: isAuthenticated', prevAuth, '->', this.state.isAuthenticated);
    }
    this.notifyListeners();
  }

  /**
   * Notify all subscribed listeners of a state change.
   */
  private notifyListeners(): void {
    const stateCopy = { ...this.state };
    this.listeners.forEach((listener) => {
      try {
        listener(stateCopy);
      } catch {
        // Listener errors shouldn't break the manager
      }
    });
  }
}

// ─── Singleton Instance ──────────────────────────────────────────────────────

/**
 * Singleton TokenManager instance.
 * Use this throughout the app for auth operations.
 */
export const TokenManager = new TokenManagerImpl();

// ─── Convenience Exports ─────────────────────────────────────────────────────

/** Initialize Amplify and attempt session restoration. */
export const configureAuth = () => TokenManager.configureAuth();

/** Login with email and password. */
export const login = (email: string, password: string) => TokenManager.login(email, password);

/** Register a new user. */
export const register = (email: string, password: string) => TokenManager.register(email, password);

/** Initiate social login. */
export const socialLogin = (provider: 'google' | 'apple') => TokenManager.socialLogin(provider);

/** Refresh the current session token. */
export const refreshToken = () => TokenManager.refreshToken();

/** Logout and clear all tokens. */
export const logout = () => TokenManager.logout();

/** Get the current ID token for API requests. */
export const getIdToken = () => TokenManager.getIdToken();

/** Check if the user is currently authenticated. */
export const isAuthenticated = () => TokenManager.isAuthenticated();
